import { useState, useRef, useCallback } from 'react'
import { useFormContext, type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { MapPinIcon } from 'lucide-react'
import {
	Button,
	FormControl, FormField, FormItem, FormLabel, FormMessage,
	Input, PhoneInput, RadioGroup,
} from '@rentflowdev/ui'
import { MapPicker } from '@/components/map-picker'
import { AddressSuggest } from '@/components/address-suggest'
import type { ResolvedAddress } from '@/lib/geo-address'
import { CREATE_PROPERTY_TYPES } from '@/types'
import type { AddPropertyFormValues } from './schema'
import { SectionHeading } from './section-heading'
import { RadioOption } from './form-options'
import {
	applyFieldResets,
	COMMERCIAL_RENT_TERM_RESETS,
	TYPE_SPECIFIC_RESETS,
} from './type-change-resets'

/**
 * Как называется номер внутри дома — по типу объекта.
 *
 * Авито отдельного поля для него не держит: у коммерции номер вписывают в
 * строку адреса. Нам он нужен отдельно, потому что договор аренды обязан
 * назвать предмет точно — «помещение №5», а не «Мясницкая, 38с4».
 *
 * Участка в списке нет: у земли нет номера внутри дома, её опознаёт
 * кадастровый номер, который спрашивается своим полем.
 */
const UNIT_NUMBER_LABEL: Record<string, string> = {
	APARTMENT: 'properties.form.apartmentNumber',
	ROOM:      'properties.form.apartmentNumber',
	OFFICE:    'properties.form.unitNumber',
	RETAIL:    'properties.form.unitNumber',
	WAREHOUSE: 'properties.form.unitNumber',
	COWORKING: 'properties.form.unitNumber',
	GARAGE:    'properties.form.garageNumber',
}

interface StepProps {
	control: Control<AddPropertyFormValues>
}

export function Step1Basics({ control }: StepProps) {
	const { t } = useTranslation()
	const form = useFormContext<AddPropertyFormValues>()
	const [mapOpen, setMapOpen] = useState(false)
	const addressRef = useRef<HTMLInputElement>(null)

	// Coordinates are the proof that an address was resolved rather than typed,
	// so they are written here and nowhere else, and cleared the moment the
	// field is edited by hand.
	const handleAddressSelect = useCallback((r: ResolvedAddress) => {
		form.setValue('address',   r.address,   { shouldDirty: true, shouldValidate: true })
		form.setValue('latitude',  r.latitude,  { shouldDirty: true, shouldValidate: true })
		form.setValue('longitude', r.longitude, { shouldDirty: true, shouldValidate: true })
		if (r.city) form.setValue('city', r.city, { shouldDirty: true, shouldValidate: true })
	}, [form])

	const handleAddressTyped = useCallback((value: string) => {
		form.setValue('address', value, { shouldDirty: true })
		form.setValue('latitude',  undefined, { shouldDirty: true })
		form.setValue('longitude', undefined, { shouldDirty: true })
	}, [form])

	const propertyType = form.watch('type')
	const lat = form.watch('latitude')
	const lng = form.watch('longitude')

	const ownerTypeOptions = [
		{ value: 'OWNER', label: t('properties.form.owner') },
		{ value: 'AGENT', label: t('properties.form.agent') },
	] as const

	return (
		<div className="flex flex-col gap-5 px-4 pt-2 pb-8">
			<section className="flex flex-col gap-2">
				<SectionHeading>{t('properties.form.propertyType')}</SectionHeading>
				<FormField
					control={control}
					name="type"
					render={({ field }) => (
						<FormItem>
							<FormControl>
								<RadioGroup
									value={field.value}
									name={field.name}
									onValueChange={(v) => {
										field.onChange(v)
										// Type-specific answers, including the commercial
										// rent terms of step 3, go back to unanswered. The
										// list is shared with the edit sheet so neither can
										// fall behind the other.
										applyFieldResets(
											form.setValue,
											TYPE_SPECIFIC_RESETS,
											COMMERCIAL_RENT_TERM_RESETS,
										)
									}}
									className="flex flex-wrap gap-2"
								>
									{CREATE_PROPERTY_TYPES.map((value) => (
										<RadioOption key={value} value={value} id={`type-${value}`} label={t(`properties.types.${value}`)} />
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</section>

			<section className="flex flex-col gap-2">
				<SectionHeading>{t('properties.form.location')}</SectionHeading>
				<FormField
					control={control}
					name="city"
					render={({ field }) => (
						<FormItem className="w-110">
							<FormLabel>{t('properties.form.city')}</FormLabel>
							<FormControl>
								<Input placeholder={t('properties.form.cityPlaceholder')} {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<div className="flex gap-2 items-start">
					<FormField
						control={control}
						name="address"
						render={({ field }) => (
							<FormItem className="w-110">
								<FormLabel>{t('properties.form.address')}</FormLabel>
								<FormControl>
									<AddressSuggest
										value={field.value}
										onChange={handleAddressTyped}
										onSelect={handleAddressSelect}
										onBlur={field.onBlur}
										name={field.name}
										allowStreetLevel={propertyType === 'LAND'}
										placeholder={t('properties.form.addressPlaceholder')}
										ref={addressRef}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					{/* Только там, где квартира вообще есть. Участок, гараж и
					    коммерция её не имеют, а поле стояло у всех девяти типов —
					    и просило номер квартиры у земли. */}
					{UNIT_NUMBER_LABEL[propertyType] && (
						<FormField
							control={control}
							name="apartmentNumber"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t(UNIT_NUMBER_LABEL[propertyType])}</FormLabel>
									<FormControl>
										<Input placeholder="5" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}
				</div>
				<Button
					type="button"
					variant="link"
					size="sm"
					iconLeft={<MapPinIcon className="size-4" />}
					onClick={() => setMapOpen(true)}
					className="h-auto p-0 text-(--color-brand)"
				>
					{lat != null && lng != null
						? t('properties.form.changeOnMap')
						: t('properties.form.showOnMap')}
				</Button>
				<MapPicker
					open={mapOpen}
					onOpenChange={setMapOpen}
					initialLat={lat}
					initialLng={lng}
					allowStreetLevel={propertyType === 'LAND'}
					onSelect={(r) => {
						form.setValue('latitude',  r.latitude,  { shouldDirty: true, shouldValidate: true })
						form.setValue('longitude', r.longitude, { shouldDirty: true, shouldValidate: true })
						if (r.city)    form.setValue('city',    r.city,    { shouldDirty: true, shouldValidate: true })
						if (r.address) form.setValue('address', r.address, { shouldDirty: true, shouldValidate: true })
					}}
				/>
			</section>

			<section className="flex flex-col gap-5">
				<SectionHeading>{t('properties.form.contacts')}</SectionHeading>
				<FormField
					control={control}
					name="ownerType"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('properties.form.advertiser')}</FormLabel>
							<FormControl>
								<RadioGroup
									value={field.value}
									onValueChange={field.onChange}
									name={field.name}
									className="flex flex-wrap gap-2"
								>
									{ownerTypeOptions.map(({ value, label }) => (
										<RadioOption key={value} value={value} id={`owner-${value}`} label={label} />
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={control}
					name="phone"
					render={({ field }) => (
						<FormItem className="w-110">
							<FormLabel>{t('properties.form.phone')}</FormLabel>
							<FormControl>
								<PhoneInput value={field.value} onChange={field.onChange} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</section>
		</div>
	)
}
